'use client'

import { motion } from 'framer-motion'

type Node = {
  code: string
  network?: string
  cx: number
  cy: number
  color: string
  highlight?: boolean
}

// Professional payment rail layout with single flow from USD to GBP
// USD/EUR on left elevated, USDC/NGN/USDT in center, GBP on right
const NODES: Node[] = [
  // Left elevated currencies
  { code: 'USD', network: undefined, cx: 100, cy: 20, color: '#4C6FE5' },
  { code: 'EUR', network: undefined, cx: 100, cy: 80, color: '#3B82C4' },
  
  // Center flowing core currencies
  { code: 'USDC', network: 'base', cx: 350, cy: 50, color: '#2775CA' },
  { code: 'NGN', network: undefined, cx: 500, cy: 50, color: '#2E8B57', highlight: true },
  { code: 'USDT', network: 'eth', cx: 650, cy: 50, color: '#26A17B' },
  
  // Right - final destination
  { code: 'GBP', network: undefined, cx: 900, cy: 50, color: '#1E64C8' },
]

const BOX_W = 72
const BOX_H = 40

// Build smooth connector paths between all nodes
function connector(a: Node, b: Node): string {
  const dx = b.cx - a.cx
  const dy = b.cy - a.cy
  
  // Smooth bezier curves with appropriate control points
  const cpx1 = a.cx + dx * 0.4
  const cpy1 = a.cy
  const cpx2 = b.cx - dx * 0.4
  const cpy2 = b.cy
  
  return `M ${a.cx} ${a.cy} C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${b.cx} ${b.cy}`
}

// Connect all nodes with smooth paths
const CONNECTORS = NODES.slice(0, -1).map((n, i) => connector(n, NODES[i + 1]))

// Create a single continuous path flowing through all currencies
const SINGLE_FLOW_PATH = NODES.slice(0, -1).reduce((path, n, i) => {
  const nextNode = NODES[i + 1]
  const dx = nextNode.cx - n.cx
  const dy = nextNode.cy - n.cy
  const cpx1 = n.cx + dx * 0.4
  const cpy1 = n.cy
  const cpx2 = nextNode.cx - dx * 0.4
  const cpy2 = nextNode.cy
  
  if (i === 0) {
    return `M ${n.cx} ${n.cy} C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${nextNode.cx} ${nextNode.cy}`
  }
  return `${path} C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${nextNode.cx} ${nextNode.cy}`
}, '')

// Create ribbon background paths for enhanced visual effect
const RIBBON_PATHS = CONNECTORS.map((path) => {
  return path.replace(/C /g, 'C ').replace(/M /g, 'M ')
})

export function PaymentRail() {
  return (
    <div className="relative w-full">
      <svg
        viewBox="0 0 1000 120"
        className="h-auto w-full"
        role="img"
        aria-label="Rombex payment rail — professional layout with elevated edge currencies"
      >
        <defs>
          <filter id="current-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Subtle connector lines */}
        {CONNECTORS.map((d, i) => (
          <g key={`ribbon-${i}`}>
            <path
              d={d}
              fill="none"
              stroke="rgba(76,111,229,0.08)"
              strokeWidth={1}
              strokeLinecap="round"
            />
          </g>
        ))}
        
        {/* Guide path - subtle background */}
        <path
          d={SINGLE_FLOW_PATH}
          fill="none"
          strokeWidth={2}
          stroke="rgba(76,111,229,0.1)"
          strokeLinecap="round"
        />
        
        {/* Single flowing dot that travels the entire path */}
        <g>
          <path
            id="flow-path"
            d={SINGLE_FLOW_PATH}
            fill="none"
            stroke="none"
          />
          <circle
            cx="0"
            cy="0"
            r="6"
            fill="#4C6FE5"
            filter="url(#current-glow)"
          >
            <animateMotion
              dur="8s"
              repeatCount="indefinite"
              keyPoints="0;1;0"
              keyTimes="0;0.5;1"
              calcMode="linear"
            >
              <mpath href="#flow-path" />
            </animateMotion>
          </circle>
        </g>



        {/* Professional currency nodes with proper styling */}
        {NODES.map((n, i) => (
          <motion.g
            key={`${n.code}-${n.network ?? ''}-${i}`}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
          >
            {/* Node background with professional styling */}
            <rect
              x={n.cx - BOX_W / 2}
              y={n.cy - BOX_H / 2}
              width={BOX_W}
              height={BOX_H}
              rx={10}
              fill={n.highlight ? '#4C6FE5' : '#ffffff'}
              stroke={n.highlight ? 'rgba(76,111,229,1)' : 'rgba(15,23,42,0.08)'}
              strokeWidth={n.highlight ? 2 : 1}
              style={{ filter: n.highlight ? 'drop-shadow(0 8px 20px rgba(76,111,229,0.3))' : 'drop-shadow(0 2px 6px rgba(15,23,42,0.08))' }}
            />
            
            {/* Color indicator dot */}
            <circle cx={n.cx - BOX_W / 2 + 14} cy={n.cy} r={5.5} fill={n.highlight ? '#ffffff' : n.color} />
            
            {/* Currency code */}
            <text
              x={n.cx - BOX_W / 2 + 26}
              y={n.cy + 5}
              fontSize={12}
              fontWeight={700}
              fill={n.highlight ? '#ffffff' : '#0f172a'}
            >
              {n.code}
            </text>
          </motion.g>
        ))}
      </svg>
    </div>
  )
}
